import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Phase 7.0 — AddLeadModal restyle.
 * VISUAL-ONLY: every data-testid, every state hook, every `api.post`, every
 * `toast` invocation, the 402 plan-limit-exceeded special case, and the
 * `onCreated(lead_id || data)` payload contract are preserved bit-for-bit.
 * Modal shell + form-field + button styling now match the Nuptial Diaries
 * Premium CRM design system used across every other screen.
 */
export default function AddLeadModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [weddingDate, setWeddingDate] = useState("");
  const [venue, setVenue] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/leads/manual", {
        full_name: name.trim(),
        phone: phone.trim(),
        wedding_date: weddingDate || null,
        venue_status: venue || null,
      });
      if (data.exists) {
        toast("Lead already exists", { description: "Opening existing record." });
      } else {
        toast.success("Lead added");
      }
      onCreated?.(data.lead_id || data);
      onClose?.();
      setName(""); setPhone(""); setWeddingDate(""); setVenue("");
    } catch (err) {
      // Module 6C.4: structured 402 (plan limit exceeded) is handled globally
      // by the Axios interceptor that pops UpgradeModal — must not toast it.
      const detail = err?.response?.data?.detail;
      const isStructuredPlanLimit =
        detail && typeof detail === "object" && detail.error === "plan_limit_exceeded";
      if (!isStructuredPlanLimit) {
        const msg = typeof detail === "string" ? detail : "Failed to add lead";
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="add-lead-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: "#FFFFFF",
          width: "100%", maxWidth: 460,
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 14,
          boxShadow: "0 20px 44px rgba(34,56,44,.20)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: 24 }}>
          <div className="alm-eyebrow">MANUAL ENTRY</div>
          <h2
            className="font-display"
            style={{ fontSize: 24, color: "#22382C", margin: "4px 0 18px", lineHeight: 1.15 }}
          >
            Add lead
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Full name" required>
              <input
                data-testid="add-lead-name" autoFocus
                value={name} onChange={(e) => setName(e.target.value)}
                className="alm-fld"
              />
            </Field>
            <Field label="Phone" required hint="10 digits, +91 added automatically">
              <input
                data-testid="add-lead-phone"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                className="alm-fld" inputMode="tel"
              />
            </Field>
            <Field label="Event date">
              <input
                data-testid="add-lead-wedding-date" type="date"
                value={weddingDate} onChange={(e) => setWeddingDate(e.target.value)}
                className="alm-fld"
              />
            </Field>
            <Field label="Event venue">
              <input
                data-testid="add-lead-venue"
                value={venue} onChange={(e) => setVenue(e.target.value)}
                className="alm-fld" placeholder="Booked / In progress / —"
              />
            </Field>
          </div>
        </div>
        <div
          style={{
            display: "flex", justifyContent: "flex-end", gap: 10,
            padding: "14px 24px",
            borderTop: "1px solid #EDE5D8",
            background: "#FAF6EC",
          }}
        >
          <button
            type="button" onClick={onClose}
            data-testid="add-lead-cancel"
            className="alm-ghost-btn"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="add-lead-save"
            className="alm-primary-btn"
          >
            {saving ? "Saving…" : "Save lead"}
          </button>
        </div>
        <style>{`
          .alm-eyebrow{
            font-size:10.5px; font-weight:700; letter-spacing:0.16em;
            text-transform:uppercase; color:#B08D55;
          }
          .alm-fld{
            width:100%; padding:10px 12px;
            background:#FCFAF5; border:1px solid #E4DCCD; border-radius:8px;
            font-family:var(--ds-body-font);
            font-size:14px; color:#2A2722; outline:none;
            transition:border-color .15s, box-shadow .15s;
          }
          .alm-fld:focus{
            border-color:#B08D55;
            box-shadow:0 0 0 3px rgba(176,141,85,.14);
          }
          .alm-primary-btn{
            padding:11px 18px; font-size:11px; font-weight:600;
            letter-spacing:0.13em; text-transform:uppercase;
            background:#22382C; color:#F4EFE4;
            border:1px solid #22382C; border-radius:6px;
            cursor:pointer; transition:background .15s;
          }
          .alm-primary-btn:hover:not(:disabled){background:#2C4A3B;}
          .alm-primary-btn:disabled{
            background:#EFE8DC; color:#B7AE9E; border-color:#EFE8DC; cursor:not-allowed;
          }
          .alm-ghost-btn{
            padding:11px 18px; font-size:11px; font-weight:600;
            letter-spacing:0.13em; text-transform:uppercase;
            background:#FFFFFF; color:#76705F;
            border:1px solid #E4DCCD; border-radius:6px;
            cursor:pointer; transition:border-color .15s, color .15s;
          }
          .alm-ghost-btn:hover{border-color:#B08D55; color:#22382C;}
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "#A39A8B", marginBottom: 6,
        }}
      >
        {label}{required && <span style={{ color: "#A35C45" }}> *</span>}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: "#A39A8B", marginTop: 4, fontStyle: "italic" }}>
          {hint}
        </div>
      )}
    </label>
  );
}
